import { View, Text, styled, ScrollView, useTheme } from 'tamagui'
import { useRef, useEffect, useState } from 'react'
import { useAppStore } from '@/store'
import { dmChannels, dmMessages } from '@/mocks/dms'
import { users } from '@/mocks'
import { DMHeader } from './DMHeader'
import { MessageItem } from '../message/MessageItem'

const Container = styled(View, {
  flex: 1,
  flexDirection: 'column',
  backgroundColor: '$surface',
  minWidth: 0,
})

const MessageArea = styled(View, {
  flex: 1,
  flexDirection: 'column',
  minHeight: 0,
})

const InputArea = styled(View, {
  paddingHorizontal: 16,
  paddingBottom: 16,
})

const InputWrapper = styled(View, {
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusLg',
  borderWidth: 1,
  borderColor: '$borderDefault',
  paddingHorizontal: 16,
  paddingVertical: 10,
  flexDirection: 'row',
  alignItems: 'center',
})

const WelcomeSection = styled(View, {
  padding: 24,
  paddingTop: 40,
  gap: 8,
})

export function DMView() {
  const { activeDmChannelId, openProfileModal } = useAppStore()
  const theme = useTheme()
  const scrollRef = useRef<any>(null)
  const [inputValue, setInputValue] = useState('')

  const dm = dmChannels.find(d => d.id === activeDmChannelId)
  const otherUserId = dm?.participantIds.find(id => id !== 'u1') || ''
  const otherUser = users[otherUserId]
  const msgs = activeDmChannelId ? dmMessages[activeDmChannelId] || [] : []

  useEffect(() => {
    // Auto-scroll to bottom
    if (scrollRef.current) {
      const el = scrollRef.current as any
      if (el.scrollToEnd) el.scrollToEnd({ animated: false })
    }
  }, [activeDmChannelId])

  if (!dm || !otherUser) {
    return (
      <Container>
        <View flex={1} alignItems="center" justifyContent="center">
          <Text fontFamily="$fontBody" fontSize={14} color="$sidebarMuted">
            Select a conversation
          </Text>
        </View>
      </Container>
    )
  }

  // Group messages (same author within 5 min = no header)
  const groupedMessages = msgs.map((msg, i) => {
    const prev = msgs[i - 1]
    const isGrouped = prev && prev.authorId === msg.authorId && (msg.timestamp - prev.timestamp) < 300000
    return { ...msg, isGrouped }
  })

  return (
    <Container>
      <DMHeader user={otherUser} />
      <MessageArea>
        <ScrollView flex={1} ref={scrollRef} showsVerticalScrollIndicator={false}>
          <WelcomeSection>
            <View
              width={64} height={64} borderRadius="$borderRadiusFull"
              backgroundColor={otherUser.roleColor || '#8B95A8'}
              alignItems="center" justifyContent="center" marginBottom={8}
            >
              <Text fontFamily="$fontHeading" fontSize={24} fontWeight="600" color="$surface">
                {otherUser.displayName[0]}
              </Text>
            </View>
            <Text fontFamily="$fontHeading" fontWeight="600" fontSize={20} color="$surfaceForeground">
              {otherUser.displayName}
            </Text>
            <Text fontFamily="$fontBody" fontSize={13} color="$sidebarMuted">
              This is the beginning of your conversation with {otherUser.displayName}.
            </Text>
          </WelcomeSection>
          <View paddingHorizontal={16} paddingBottom={8}>
            {groupedMessages.map((msg) => (
              <MessageItem
                key={msg.id}
                message={msg}
                isGrouped={msg.isGrouped}
                onOpenThread={() => {}}
                onOpenProfile={openProfileModal}
              />
            ))}
          </View>
        </ScrollView>
      </MessageArea>
      <InputArea>
        <InputWrapper
          focusStyle={{ borderColor: '$accent' }}
          // @ts-expect-error -- web transition
          style={{ transition: 'border-color 200ms ease' }}
        >
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                setInputValue('')
              }
            }}
            placeholder={`Message ${otherUser.displayName}`}
            rows={1}
            style={{
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: theme.surfaceForeground?.val || '#E2E8F0',
              fontFamily: '$fontMono',
              fontSize: 14,
              width: '100%',
              resize: 'none',
              lineHeight: '20px',
            }}
          />
        </InputWrapper>
      </InputArea>
    </Container>
  )
}
